import { useState, useCallback } from 'react';
import { getOmbudsmanDecisionById, getDecisionByReference } from '../utils/api';
import { isDRNReference, isUUID } from '../utils/decisionLinks';
import { OmbudsmanDecision } from '../types/ombudsman';

interface UseDecisionModalReturn {
  isOpen: boolean;
  isLoading: boolean;
  decision: OmbudsmanDecision | null;
  error: string | null;
  decisionId: string | null;
  openDecision: (decisionIdOrReference: string) => Promise<void>;
  closeDecision: () => void;
}

/**
 * Hook to manage Ombudsman decision modal state and data fetching
 * 
 * Supports both decision IDs (UUID) and references (DRN-xxxxx format)
 */
export function useDecisionModal(): UseDecisionModalReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [decision, setDecision] = useState<OmbudsmanDecision | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decisionId, setDecisionId] = useState<string | null>(null);

  const openDecision = useCallback(async (decisionIdOrReference: string) => {
    if (!decisionIdOrReference) {
      setError('No decision ID or reference provided');
      return;
    }

    setIsOpen(true);
    setIsLoading(true);
    setError(null);
    setDecisionId(decisionIdOrReference);

    try {
      let decisionData: OmbudsmanDecision;

      // Validate that we have a DRN or UUID - descriptive text won't work
      if (!isDRNReference(decisionIdOrReference) && !isUUID(decisionIdOrReference)) {
        // This is likely descriptive text, not a valid DRN
        throw new Error(
          `Invalid decision reference: "${decisionIdOrReference}". ` +
          `Expected a DRN (e.g., DRN-1234567) or UUID. ` +
          `This may be a descriptive reference - please use the DRN number from the precedent card.`
        );
      }

      // Determine if it's a DRN reference or UUID
      if (isDRNReference(decisionIdOrReference)) {
        // Fetch by reference (DRN format)
        decisionData = await getDecisionByReference(decisionIdOrReference) as OmbudsmanDecision;
      } else if (isUUID(decisionIdOrReference)) {
        // Fetch by ID (UUID format)
        decisionData = await getOmbudsmanDecisionById(decisionIdOrReference) as OmbudsmanDecision;
      } else {
        // Fallback: Try as reference first, then ID
        try {
          decisionData = await getDecisionByReference(decisionIdOrReference) as OmbudsmanDecision;
        } catch {
          decisionData = await getOmbudsmanDecisionById(decisionIdOrReference) as OmbudsmanDecision;
        }
      }

      setDecision(decisionData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch decision:', err);
      setError(err instanceof Error ? err.message : 'Failed to load decision');
      setDecision(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const closeDecision = useCallback(() => {
    setIsOpen(false);
    // Clear state after a short delay to allow animation to complete
    setTimeout(() => {
      setDecision(null);
      setError(null);
      setDecisionId(null);
      setIsLoading(false);
    }, 200);
  }, []);

  return {
    isOpen,
    isLoading,
    decision,
    error,
    decisionId,
    openDecision,
    closeDecision,
  };
}

